import { getAriaLabel } from '@bridge/utils';
import { FlexContainer, FormControl, FormDescription, FormItem, FormLabel, FormMessage, Switch, Tooltip, TooltipContent, TooltipTrigger } from '../../components';
import { getFieldIds } from './utils';
import type { FieldValues } from 'react-hook-form';
import type { BaseComponentProps, BaseFieldProps } from './types';
export type SwitchComponentProps = BaseComponentProps<typeof Switch> & {
  disabledTooltip?: React.ReactNode;
  labelPosition?: 'left' | 'right';
};
interface SwitchFieldProps<TFieldValues extends FieldValues> extends BaseFieldProps<TFieldValues> {
  componentProps: SwitchComponentProps;
}
export function SwitchField<TFieldValues extends FieldValues>({
  field,
  fieldConfig,
  fieldState,
  componentProps,
  labelSize
}: SwitchFieldProps<TFieldValues>): React.ReactElement {
  const {
    id,
    disabled: componentDisabled,
    disabledTooltip,
    labelPosition = 'right',
    ...restComponentProps
  } = componentProps;
  const isDisabled = field.disabled || componentDisabled;
  const {
    fieldId,
    labelId,
    descriptionId,
    errorId,
    describedBy,
    isInvalid
  } = getFieldIds(fieldConfig, fieldState, id);
  const switchElement = <FormControl>
      <Switch {...restComponentProps} aria-describedby={describedBy} aria-invalid={isInvalid} aria-label={getAriaLabel(fieldConfig.label)} checked={Boolean(field.value)} disabled={isDisabled} id={fieldId} onCheckedChange={field.onChange} ref={field.ref} />
    </FormControl>;
  const labelElement = <FlexContainer className="gap-0.5" direction="column" shrink>
      <FormLabel htmlFor={fieldId} id={labelId} required={fieldConfig.required} size={labelSize} weight="medium">
        {fieldConfig.label}
      </FormLabel>
      {descriptionId && <label htmlFor={fieldId}>
          <FormDescription id={descriptionId}>{fieldConfig.description}</FormDescription>
        </label>}
    </FlexContainer>;
  const fieldContent = <div className={`flex items-center pt-2 ${labelPosition === 'left' ? 'w-full justify-between' : 'space-x-2'}`}>
      {labelPosition === 'left' ? <>
          {labelElement}
          {switchElement}
        </> : <>
          {switchElement}
          {labelElement}
        </>}
    </div>;
  const fieldWithTooltip = disabledTooltip && isDisabled ? <Tooltip>
        <TooltipTrigger asChild>{fieldContent}</TooltipTrigger>
        <TooltipContent>{disabledTooltip}</TooltipContent>
      </Tooltip> : fieldContent;
  return <FormItem className="flex-1">
      {fieldWithTooltip}
      {errorId && <FormMessage id={errorId}>{fieldState.error?.message}</FormMessage>}
    </FormItem>;
}